import { useSelector } from 'react-redux';
import { openPrintPreview } from '../../helper/printPreviewHelper';

export const usePrintReport = () => {
  const { data: { user } = {} } = useSelector((state) => state.auth || {});

  const getCurrentUserName = () => {
    if (!user) return 'System';
    return user.managerName || user.doctorName || user.adminName || user.name || 'Unknown User';
  };

  const printReport = (order, onPrint) => {
    if (!order) {
      console.error('No order data provided for printing');
      return;
    }

    const groupedTests = {};
    if (order.tests) {
      order.tests.forEach(t => {
        const category = t?.test?.testGroup?.categoryName || 'OTHERS';
        if (!groupedTests[category]) {
          groupedTests[category] = [];
        }
        groupedTests[category].push(t);
      });
    }

    const now = new Date();
    const entryDate = now.toLocaleDateString('en-GB');
    const entryTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });

    let deliveryTime = '';
    if (order.time) {
      let [h, m] = order.time.replace('.', ':').split(':');
      h = parseInt(h, 10);
      m = parseInt(m, 10) || 0;
      const start = new Date();
      start.setHours(h, m, 0, 0);
      const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
      deliveryTime = `${start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })} to ${end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
    }

    const baseAmount = order.baseAmount || order.totalAmount || 0;
    const discountAmount = order.discountAmount || 0;
    const totalWithVat = baseAmount - discountAmount;
    const paidAmount = order.paidAmount || 0;
    let dueAmount = order.dueAmount || (totalWithVat - paidAmount);
    if (dueAmount < 0) dueAmount = 0;

    const renderReceipt = (bgColor, label) => `
      <div class="receipt-copy" style="background:${bgColor};padding:18px 18px 10px 18px;margin-bottom:18px;border-radius:8px;box-shadow:0 1px 4px #bbb;position:relative;min-height:680px;">
        <div style="position:absolute;top:4px;right:20px;font-size:13px;font-weight:bold;color:#333;opacity:0.7;">${label}</div>
        <div class="header-box">
          <div class="clinic-name">MEDIPLUS MEDICAL SERVICES (Pvt) LTD.</div>
          <div class="clinic-address">253/1 President Road, Narayangonj</div>
          <div class="clinic-subtitle"> Phone: 7633972, 01956699976 </div>
        </div>
        <div class="patient-info">
          <div class="patient-left">
            <div class="info-line"><strong>Name</strong> ${order.patient?.patientName || 'N/A'}</div>
            <div class="info-line"><strong>Contact</strong> ${order.patient?.mobile || 'N/A'}</div>
            <div class="info-line"><strong>Age</strong> ${order.patient?.age || 'N/A'} </div>
            <div class="info-line"><strong>Sex</strong> ${order.patient?.gender || 'N/A'}</div>
            <div class="info-line"><strong>Refd By</strong> ${order.doctor?.docName || 'Self'}</div>
          </div>
          <div class="patient-right">
            <div class="info-line"><strong>Test Order ID</strong> ${order.testOrderID || 'N/A'}</div>
            <div class="info-line"><strong>Entry Date</strong> ${entryDate}</div>
            <div class="info-line"><strong>Entry Time</strong> ${entryTime}</div>
            <div class="info-line"><strong>Delivery Date</strong> ${new Date(new Date(order.date).getTime() + 24 * 60 * 60 * 1000).toLocaleDateString('en-GB')}</div>
            <div class="info-line"><strong>Delivery Time</strong> ${deliveryTime}</div>
          </div>
        </div>
        <table class="tests-table">
          <thead class="table-header">
            <tr>
              <th style="width: 150px;">Test Group Name </th>
              <th>Test Name</th>
              <th style="width: 80px;">Taka</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(groupedTests).map(([category, categoryTests]) => {
      const categoryDisplayName = category.toUpperCase();
      return categoryTests.map((test, index) => {
        if (index === 0) {
          return `
                    <tr class="test-row">
                      <td rowspan="${categoryTests.length}" class="group-row">${categoryDisplayName.toUpperCase()}</td>
                      <td class="test-name-col">${test.test?.title || 'N/A'}</td>
                      <td class="price-col">${test.testPrice ? test.testPrice.toFixed(2) : '0.00'}</td>
                    </tr>
                  `;
        } else {
          return `
                    <tr class="test-row">
                      <td class="test-name-col">${test.test?.title || 'N/A'}</td>
                      <td class="price-col">${test.testPrice ? test.testPrice.toFixed(2) : '0.00'}</td>
                    </tr>
                  `;
        }
      }).join('');
    }).join('')}
          </tbody>
        </table>
        <div class="totals-section">
          <div class="received-by">
            <strong>Received By:</strong> ${getCurrentUserName()}
            <div style="margin-top: 50px;">
              <div style="border-top: 1px solid #000; width: 150px; margin-bottom: 5px;"></div>
              <strong>Signature</strong>
            </div>
          </div>
          <div class="totals-box">
            <div class="total-line"><strong>Total:</strong> ${baseAmount.toFixed(2)}</div>
            <div class="total-line"><strong>Discount:</strong> ${discountAmount.toFixed(2)}</div>
            <br/>
            <div style="border-top: 1px solid #000; width: 150px; margin-bottom: 5px;"></div>
            <div class="total-line"><strong>Total+Vat:</strong> ${totalWithVat.toFixed(2)}</div>
            <div class="total-line"><strong>Advance:</strong> ${paidAmount.toFixed(2)}</div>
            <div class="total-line"><strong>Due:</strong> ${dueAmount.toFixed(2)}</div>
          </div>
        </div>
      </div>
    `;

    const patientName = order.patient?.patientName || order.patientName || 'Unknown';
    const orderId = order.testOrderID || order.testOrderId || order._id || 'N/A';
    const printTitle = `Test Receipt - ${patientName} - ${orderId}`;

    const htmlContent = `
      <html>
      <head>
        <title>${printTitle}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 20px; 
            font-size: 12px;
            line-height: 1.3;
            background: #eee;
          }
          .header-box {
            border: 2px solid #000;
            text-align: center;
            padding: 10px;
            margin-bottom: 15px;
            background: #fff;
          }
          .clinic-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 2px;
          }
          .clinic-subtitle {
            font-size: 12px;
            margin-bottom: 3px;
          }
          .clinic-address {
            font-size: 11px;
          }
          .patient-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            font-size: 11px;
          }
          .patient-left, .patient-right {
            width: 48%;
          }
          .info-line {
            margin-bottom: 3px;
          }
          .info-line strong {
            display: inline-block;
            width: 90px;
          }
          .tests-table {
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #000;
            margin-bottom: 20px;
          }
          .table-header {
            background-color: #f5f5f5;
            border: 1px solid #000;
          }
          .table-header th {
            padding: 8px;
            font-size: 11px;
            font-weight: bold;
            border-right: 1px solid #000;
            text-align: center;
          }
          .group-row {
            background-color: #f9f9f9;
            font-weight: bold;
            font-size: 11px;
          }
          .group-row td {
            padding: 6px 8px;
            border-right: 1px solid #000;
            border-bottom: 1px solid #000;
          }
          .test-row td {
            padding: 4px 8px;
            font-size: 11px;
            border-right: 1px solid #000;
            border-bottom: 1px solid #000;
            vertical-align: top;
          }
          .test-name-col {
            text-align: left;
          }
          .price-col {
            text-align: right;
            width: 80px;
          }
          .totals-section {
            margin-top: 15px;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }
          .received-by {
            font-size: 11px;
          }
          .totals-box {
            font-size: 11px;
            text-align: right;
          }
          .total-line {
            margin-bottom: 3px;
          }
          @media print {
            body {
              margin: 0;
              background: #fff;
            }
          }
          @page {
            margin: 12mm 10mm;
            size: A4;
          }
          .receipt-copy {
            page-break-inside: avoid;
          }
        </style>
      </head>
      <body>
        ${renderReceipt('#fff', 'Patient Copy (White)')}
        ${renderReceipt('#e3f0ff', 'Office Copy (Blue)')}
        ${renderReceipt('#fffbe0', 'Lab Copy (Yellow)')}
      </body>
      </html>
    `;

    openPrintPreview(htmlContent, printTitle);

    if (onPrint) {
      onPrint();
    }
  };

  return { printReport };
};

export default usePrintReport;